import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Stack, Spinner, SpinnerSize, IChoiceGroupOption, ChoiceGroup, TextField, PrimaryButton, MessageBar, MessageBarType } from "@fluentui/react";
import Auth from "../../Auth/Auth";
import { Redirect, RouteComponentProps, useHistory, useLocation } from "react-router";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { useForm, Controller as ReactHookFormController } from "react-hook-form";
import * as Lay from "./styles/RefundsStyles";
import { AmountDisclaimerWrapper, AutoSwitchWarningWrapper } from "./styles/RefundsStyles";
import FundService, { RefundEstimationDates } from "@/services/FundService";
import { InvestmentFundFinder } from "../PurchaseTransferScreen/components/InvestmentFundFinder";
import FinametrixService from "@/services/FinametrixService";
import InputRefundsButton from "./components/InputRefundsButton";
import OrderService, { OutdatedRefundsRecharge } from "@/services/OrderService";
import UserIdentitiesService from "@/services/UserIdentitiesService";
import DocumentTitle from "react-document-title";
import React from "react";
import "./styles/Refunds.css";
import { RefundTimeLine } from "./components/RefundTimeLine";
import users from "@/services/UserService";
import { AccountsAutorized } from "@/services/SubscriptionService";
import DropdownAccounts from "./components/DropdownAccounts";
import * as DropdownStyles from "./styles/DropdownAccountsStyles";
import { currencyNeutreFormatter } from "@/utils/numberFormatter";
import { getUsableMainIdentityId } from "@/utils/mainUserIdentityRules";
import { ErrorModal } from "@/components/Modals/ErrorModal";
import { filterRefundPortfolioFunds, buildPortfolioLimitsByIsin, isIsinInPortfolioFunds, mapRefundablePortfolioFunds, parseOutdatedRefundsRecharge, PortfolioFundLimits, resolveRefundPositionLimits, resolveAutoSwitchToTotal, arePositionLimitsUsable, isRefundAmountType, isRefundAmountWithinMax, mapAuthorizedRefundAccounts, resolveRefundIban, getRefundIbanSelectionError, RefundIbanRequiredMessage, hasCompleteRefundEstimationDates, ProcessErrorContactMessage, resolveCreateOrderCatch, AutoSwitchWarning } from "./refundLimits";

// RHF 7 + TS 4.9: el genérico de Controller recorre Path/FieldValues en bucle.
const Controller: any = ReactHookFormController;

const refundsOptions: IChoiceGroupOption[] =
[
    { key: "0", text: "Por Importe", value: "amount" },
    { key: "1", text: "Por Participaciones", value: "titles" },
    { key: "2", text: "Total", value: "total" },
];

interface RouteParams {}
interface Props extends RouteComponentProps<RouteParams> {}
export interface Accounts
{
    id: number;
    value: string;
    default: boolean;
}

type RefundErrorField = "sourceFund" | "refundKey" | "iban" | "order" | "simulation";
type RefundFormError = {field: RefundErrorField; message: string};

const SupportEmail = "info@ironia.tech";

const RefundsSupportRetryHint = (props: {indication: string}) =>
(
    <span>
        {" "}Vuelve a intentarlo. Si el problema continúa, ponte en contacto con{" "}
        <a href={`mailto:${SupportEmail}`}>{SupportEmail}</a>
        {" "}indicando {props.indication}.
    </span>
);

export const Refunds: React.FunctionComponent<Props> = () =>
{
    const theme = useContext(ThemeContext);
    const { state } = useLocation<any>();
    const history = useHistory();
    const { handleSubmit, watch, formState: { errors }, control, getValues, setValue, trigger, clearErrors } = useForm<any>();
    const watchSourceFund = watch("sourceFund");
    const sourceFundErrorMessage = errors.sourceFund == null ? undefined : (errors.sourceFund.type === "required" ? "Debe seleccionar un fondo de origen." : "El fondo de origen no es válido.");
    const watchRefundKey = watch("refundKey");
    const watchAmount = watch("amount", "");
    const watchAmountType = watch("amountType");
    const [sending, setSending] = useState(false);
    const [portfolioFundsLoaded, setPortfolioFundsLoaded] = useState(false);
    const [portfolioFunds, setPortfolioFunds] = useState<any[]>([]);
    const [refundCode, setRefundCode] = useState<string>("");
    const [hasRefundCode, setHasRefundCode] = useState<boolean>(false);
    const [refundIBAN, setRefundIBAN] = useState<string>("");
    const [accounts, setAccounts] = useState<Accounts[]>([]);
    const [accountsLoaded, setAccountsLoaded] = useState<boolean>(false);
    const [formError, setFormError] = useState<RefundFormError | null>(null);
    const [errorModal, setErrorModal] = useState<boolean>(false);
    const [firstTime, setFirstTime] = useState<boolean>(true);
    const [simulation, setSimulation] = useState<boolean>(false);
    const [loadingSimulation, setLoadingSimulation] = useState<boolean>(false);
    const [refundCodeCheckFailed, setRefundCodeCheckFailed] = useState<boolean>(false);
    const [accountsLoadFailed, setAccountsLoadFailed] = useState<boolean>(false);
    const [ibanRetryNonce, setIbanRetryNonce] = useState(0);
    const [refundDates, setRefundDates] = useState<RefundEstimationDates>({finalProceso: "", ventaLiquidacion: "", ventaValor: ""});
    const [portfolioLimitsByIsin, setPortfolioLimitsByIsin] = useState<Record<string, PortfolioFundLimits>>({});
    const [autoSwitchWarning, setAutoSwitchWarning] = useState<AutoSwitchWarning>("none");
    //const patternIBAN = "(([E+S]|[e+s]){2})\t*(\\d{2})\\s*\t*(\\d{4})\\s*\t*(\\d{4})\\s*\t*(\\d{2})\\s*\t*(\\d{10})";
    const mounted = useRef(false);
    const portfolioLoadListenersRef = useRef<((funds: any[]) => void)[]>([]);
    const portfolioLoadedTimeoutRef = useRef<number | undefined>(undefined);
    const lastValidatedFundIsinRef = useRef<string | null>(null);
    const ibanRequestIdRef = useRef(0);
    const portfolioRequestIdRef = useRef(0);
    const refundCodeRequestIdRef = useRef(0);
    const outdatedRequestIdRef = useRef(0);
    const hasRestoredFormRef = useRef(false);
    const user = Auth.getUserProfile();
    const reduxActivePlan = useSelector((state: RootStateOrAny) => state.auth.userActivePlan);
    const activePlan = useMemo(() =>
    {
        if(reduxActivePlan?.portfolio != null)
        {
            return reduxActivePlan;
        }
        const storedPlan = Auth.getActivePlan();
        if(storedPlan?.portfolio != null)
        {
            return storedPlan;
        }
        return reduxActivePlan;
    }, [reduxActivePlan]);
    const outdatedContract = useMemo(() => activePlan?.outdatedContract === true, [activePlan]);
    const cancelled = useMemo(() => activePlan?.sync === true && activePlan?.cancelled === true, [activePlan]);
    const outdated = useMemo(() => activePlan?.sync === true && activePlan?.outdated === true, [activePlan]);
    const [outdatedPrice, setOutdatedPrice] = useState<number | null>(null);
    const [outdatedEndDate, setOutdatedEndDate] = useState<string | null>(null);
    const [outdatedDaysExpired, setOutdatedDaysExpired] = useState<number | null>(null);
    const [outdatedRechargeFailed, setOutdatedRechargeFailed] = useState<boolean>(false);
    const hasFetchedOutdatedData = useRef(false);
    const outdatedRechargeInFlight = useRef(false);
    const cancelledForm = state?.cancelledFormState ?? state;
    const navigationOrigin: any = cancelledForm?.origin ?? state?.origin;
    const userId = user?.id;
    const mainIdentityId = getUsableMainIdentityId(user?.mainUserIdentity);
    const activePlanId = activePlan?.id;
    const finametrixId = activePlan?.portfolio?.finametrixId;
    const restoredIban = cancelledForm?.refundIBAN;
    const restoredAmountType = cancelledForm?.amountType;
    const restoredAmount = cancelledForm?.amount;
    const navigationOriginIsin = typeof navigationOrigin?.isin === "string" ? navigationOrigin.isin : undefined;
    const selectedIsin: string | undefined = watchSourceFund?.isin ?? (!portfolioFundsLoaded ? navigationOrigin?.isin : undefined);
    const selectedLimits = resolveRefundPositionLimits(selectedIsin, portfolioLimitsByIsin, navigationOrigin);
    const title: number = selectedLimits.titles;
    const amount: number = selectedLimits.importe;
    const isSelectedFundInPortfolio = isIsinInPortfolioFunds(watchSourceFund?.isin, portfolioFunds);
    const simulationAvailable = watchSourceFund !== undefined && isSelectedFundInPortfolio;
    const portfolioReady = portfolioFundsLoaded && isSelectedFundInPortfolio;
    const finderDefaultOrigin = !portfolioFundsLoaded ? navigationOrigin : portfolioFunds.find((fund) => fund.isin === navigationOrigin?.isin);
    const errorFor = (field: RefundErrorField) => formError?.field === field ? formError.message : "";
    const displayedSourceFundError = portfolioFundsLoaded && watchSourceFund?.isin != null && !isSelectedFundInPortfolio ? "El fondo seleccionado no forma parte de tu cartera." : (sourceFundErrorMessage ?? (errorFor("sourceFund") || undefined));
    const ibanSelectionError = accountsLoaded && !accountsLoadFailed ? getRefundIbanSelectionError(accounts, refundIBAN) : "";
    const displayedIbanError = errorFor("iban") !== "" ? errorFor("iban") : ibanSelectionError;

    const getRefundsFormState = () =>
    ({
        origin: watchSourceFund ?? navigationOrigin,
        amountType: watchAmountType,
        amount: watchAmount,
        refundIBAN
    });

    const checkRefundCode = useCallback(async () =>
    {
        const requestId = ++refundCodeRequestIdRef.current;
        setFirstTime(true);
        setRefundCodeCheckFailed(false);
        // Sin principal identificado (incluido el principal vacío del registro) no se puede comprobar la clave
        if(mainIdentityId === undefined)
        {
            if(mounted.current && requestId === refundCodeRequestIdRef.current)
            {
                setRefundCodeCheckFailed(true);
                setFirstTime(false);
            }
            return;
        }
        try
        {
            const hasDefinedCode = await UserIdentitiesService.isRefundCodeDefined(mainIdentityId);
            if(!mounted.current || requestId !== refundCodeRequestIdRef.current)
            {
                return;
            }
            setHasRefundCode(hasDefinedCode === true);
            setRefundCode("");
            setRefundCodeCheckFailed(false);
            setFirstTime(false);
        }
        catch
        {
            if(!mounted.current || requestId !== refundCodeRequestIdRef.current)
            {
                return;
            }
            setRefundCodeCheckFailed(true);
            setFirstTime(false);
        }
    }, [mainIdentityId]);
    const onChangeIBANValue = React.useCallback((newValue?: string) =>
    {
        setRefundIBAN(newValue as string);
        setFormError((current) => current?.field === "iban" ? null : current);
        // Validación de formato IBAN comentada a propósito: el submit sigue
        // exigiendo 24 caracteres mientras esa comprobación no se reactive.
        // if(newValue !== undefined)
        // {
        //     newValue = newValue.replaceAll(/\s/g, '');
        // }
        // if(newValue !== undefined && newValue.length === 24)
        // {
        //     let newReg = new RegExp(patternIBAN);
        //     if(newValue?.match(newReg))
        //     {
        //         setRefundIBAN(newValue);
        //         onChangeError("");
        //     }
        //     else
        //     {
        //         onChangeError("Introduzca un IBAN válido");
        //     }
        // }
        // else if(newValue !== undefined && newValue.length > 24)
        // {
        //     setRefundIBAN(newValue);
        //     onChangeError("El IBAN debe contener 24 dígitos como máximo, con 2 letras al comienzo y 22 números al final.");
        // }
        // else
        // {
        //     setRefundIBAN(newValue || "");
        //     onChangeError("");
        // }
    }, []);
    useEffect(() =>
    {
        mounted.current = true;
        return () =>
        {
            mounted.current = false;
            if(portfolioLoadedTimeoutRef.current !== undefined)
            {
                window.clearTimeout(portfolioLoadedTimeoutRef.current);
            }
        };
    }, []);
    useEffect(() =>
    {
        checkRefundCode();
        return () =>
        {
            refundCodeRequestIdRef.current += 1;
        };
    }, [checkRefundCode]);
    useEffect(() =>
    {
        const requestId = ++ibanRequestIdRef.current;
        if(!(userId > 0) || activePlanId == null)
        {
            setAccounts([]);
            setRefundIBAN("");
            setAccountsLoaded(true);
            setAccountsLoadFailed(false);
            return;
        }

        setAccountsLoaded(false);
        setAccountsLoadFailed(false);
        users.getBankAccountsAuthorizedBySavingPlanId(userId, activePlanId).then((response: AccountsAutorized[]) =>
        {
            if(!mounted.current || requestId !== ibanRequestIdRef.current)
            {
                return;
            }
            const mapped = mapAuthorizedRefundAccounts(response);
            const selected = resolveRefundIban(mapped, restoredIban);
            setAccounts(mapped);
            setRefundIBAN(selected);
            setAccountsLoaded(true);
            setAccountsLoadFailed(false);
        })
        .catch(() =>
        {
            if(!mounted.current || requestId !== ibanRequestIdRef.current)
            {
                return;
            }
            setAccounts([]);
            setRefundIBAN("");
            setAccountsLoaded(true);
            setAccountsLoadFailed(true);
        });
        return () =>
        {
            ibanRequestIdRef.current += 1;
        };
    }, [userId, activePlanId, restoredIban, ibanRetryNonce]);
    const onPortfolioLoaded = (cb: (funds: any[]) => void) =>
    {
        if(typeof cb !== "function")
        {
            return;
        }
        if(portfolioFundsLoaded)
        {
            cb(portfolioFunds);
            return;
        }
        portfolioLoadListenersRef.current.push(cb);
    };
    const loadPortfolioFunds = useCallback(async (requestId: number) =>
    {
        try
        {
            if(!finametrixId)
            {
                return;
            }
            const finametrixPortfolio = await FinametrixService.getPortfolio(finametrixId).catch(() => undefined);
            if(requestId !== portfolioRequestIdRef.current)
            {
                return;
            }

            if(typeof finametrixPortfolio?.creationDate !== "string")
            {
                return;
            }

            const fromDate = finametrixPortfolio?.creationDate?.substring(0, 10).replace(/-/g, "");
            const date_to = function (this: any)
            {
                this.setDate(this.getDate() - 1);
                return this;
            }
            .call(new Date());

            const toDate = date_to.getFullYear().toString() + ("0" + (date_to.getMonth() + 1)).slice(-2) + ("0" + date_to.getDate()).slice(-2);

            const reporting = await FinametrixService.getReporting(finametrixId, fromDate, toDate).catch(() => undefined);
            if(requestId !== portfolioRequestIdRef.current)
            {
                return;
            }

            if(!Array.isArray(reporting?.positions?.positionsTo?.positions) || reporting.positions.positionsTo.positions.length <= 0)
            {
                return;
            }

            const positions = reporting.positions.positionsTo.positions;
            const limitsByIsin = buildPortfolioLimitsByIsin(positions);
            setPortfolioLimitsByIsin(limitsByIsin);

            const fundsFromPositions = mapRefundablePortfolioFunds(positions);
            setPortfolioFunds(fundsFromPositions);

            const currentIsin = getValues("sourceFund")?.isin ?? navigationOriginIsin;
            const matched = typeof currentIsin === "string"
                ? fundsFromPositions.find((fund) => fund.isin === currentIsin)
                : undefined;
            if(matched != null)
            {
                setValue("sourceFund", matched);
            }
            else if(getValues("sourceFund")?.isin != null)
            {
                setValue("sourceFund", undefined);
            }
        }
        catch
        {
            // fallo general: no romper el flujo; portfolioFundsLoaded se marca en finally del useEffect
        }
    }, [finametrixId, navigationOriginIsin, getValues, setValue]);
    const loadRefundFinderData = (filter: any): Promise<any[]> =>
    {
        return new Promise((resolve) =>
        {
            const apply = (funds: any[]) =>
            {
                setTimeout(() =>
                {
                    resolve(filterRefundPortfolioFunds(funds, filter));
                }, 100);
            };
            if(portfolioFundsLoaded)
            {
                apply(portfolioFunds);
            }
            else
            {
                onPortfolioLoaded(apply);
            }
        });
    };
    useEffect(() =>
    {
        if(!portfolioFundsLoaded)
        {
            return;
        }
        const pending = portfolioLoadListenersRef.current;
        portfolioLoadListenersRef.current = [];
        pending.forEach((cb) =>
        {
            cb(portfolioFunds);
        });
    }, [portfolioFundsLoaded, portfolioFunds]);
    useEffect(() =>
    {
        if(hasRestoredFormRef.current)
        {
            return;
        }
        if(!isRefundAmountType(restoredAmountType) && restoredAmount == null)
        {
            return;
        }
        hasRestoredFormRef.current = true;
        if(isRefundAmountType(restoredAmountType))
        {
            setValue("amountType", restoredAmountType);
        }
        if(restoredAmount != null)
        {
            setValue("amount", restoredAmount);
        }
    }, [restoredAmountType, restoredAmount, setValue]);
    useEffect(() =>
    {
        const requestId = ++portfolioRequestIdRef.current;
        setPortfolioFundsLoaded(false);
        loadPortfolioFunds(requestId).finally(() =>
        {
            if(!mounted.current || requestId !== portfolioRequestIdRef.current)
            {
                return;
            }
            portfolioLoadedTimeoutRef.current = window.setTimeout(() =>
            {
                if(mounted.current && requestId === portfolioRequestIdRef.current)
                {
                    setPortfolioFundsLoaded(true);
                }
            }, 0);
        });
        return () =>
        {
            portfolioRequestIdRef.current += 1;
            if(portfolioLoadedTimeoutRef.current !== undefined)
            {
                window.clearTimeout(portfolioLoadedTimeoutRef.current);
            }
        };
    }, [loadPortfolioFunds]);
    useEffect(() =>
    {
        window.scrollTo(0, 0);
    }, []);
    useEffect(() =>
    {
        if(watchAmountType !== "amount" && watchAmountType !== "titles")
        {
            return;
        }
        const amountNum = parseFloat(String(watchAmount ?? ""));
        const autoSwitch = resolveAutoSwitchToTotal(watchAmountType, amountNum, amount, title);
        if(autoSwitch.shouldSwitch)
        {
            setValue("amountType", "total");
            setValue("amount", "");
            setAutoSwitchWarning(autoSwitch.warning);
            return;
        }
        setAutoSwitchWarning("none");
    }, [watchAmount, watchAmountType, amount, title, setValue]);
    useEffect(() =>
    {
        if(watchAmountType !== "amount" && watchAmountType !== "titles")
        {
            return;
        }
        const entered = String(watchAmount ?? "").trim();
        if(entered === "")
        {
            return;
        }
        trigger("amount");
    }, [amount, title, watchAmount, watchAmountType, portfolioFundsLoaded, isSelectedFundInPortfolio, trigger]);
    const requestOutdatedRecharge = useCallback((force: boolean) =>
    {
        if(outdated !== true)
        {
            return;
        }
        if(outdatedRechargeInFlight.current)
        {
            return;
        }
        if(!force && hasFetchedOutdatedData.current)
        {
            return;
        }
        if(force && outdatedPrice != null)
        {
            return;
        }

        const savingsPlanId = Number(activePlanId ?? activePlan?.planId);
        if(!Number.isFinite(savingsPlanId) || savingsPlanId <= 0)
        {
            if(activePlan?.sync === true)
            {
                hasFetchedOutdatedData.current = true;
                setOutdatedRechargeFailed(true);
            }
            return;
        }

        hasFetchedOutdatedData.current = true;
        outdatedRechargeInFlight.current = true;
        const requestId = ++outdatedRequestIdRef.current;
        if(force)
        {
            setOutdatedRechargeFailed(false);
            setOutdatedPrice(null);
            setOutdatedEndDate(null);
            setOutdatedDaysExpired(null);
        }

        OrderService.getSubscriptionEndDateAndRecharge(savingsPlanId).then((response: OutdatedRefundsRecharge) =>
        {
            if(!mounted.current || requestId !== outdatedRequestIdRef.current)
            {
                return;
            }
            const parsed = parseOutdatedRefundsRecharge(response);
            if(parsed == null)
            {
                setOutdatedRechargeFailed(true);
                return;
            }
            setOutdatedRechargeFailed(false);
            setOutdatedEndDate(parsed.endDate);
            setOutdatedPrice(parsed.recharge);
            setOutdatedDaysExpired(parsed.daysExpired);
        })
        .catch(() =>
        {
            if(mounted.current && requestId === outdatedRequestIdRef.current)
            {
                setOutdatedRechargeFailed(true);
            }
        })
        .finally(() =>
        {
            if(requestId === outdatedRequestIdRef.current)
            {
                outdatedRechargeInFlight.current = false;
            }
        });
    }, [outdated, activePlanId, activePlan?.planId, activePlan?.sync, outdatedPrice]);
    useEffect(() =>
    {
        hasFetchedOutdatedData.current = false;
        outdatedRechargeInFlight.current = false;
    }, [activePlanId, activePlan?.planId]);
    useEffect(() =>
    {
        requestOutdatedRecharge(false);
    }, [requestOutdatedRecharge]);
    const onRefundCodeEstablished = () =>
    {
        setHasRefundCode(true);
        setRefundCode("");
        setValue("refundKey", "");
        // Tras fijar la clave, si el plan está caducado y aún no hay recargo, se vuelve a consultar.
        if(outdated === true && (outdatedRechargeFailed === true || outdatedPrice == null))
        {
            requestOutdatedRecharge(true);
        }
    };
    const handleChange = (refund?: string) =>
    {
        const val = refund ?? "";
        if(val !== "")
        {
            onRefundCodeEstablished();
        }
        else
        {
            setRefundCode("");
        }
    };
    const handleSimulation = () =>
    {
        if(watchSourceFund !== undefined && isSelectedFundInPortfolio)
        {
            setFormError((current) => current?.field === "simulation" ? null : current);
            setLoadingSimulation(true);
            if(typeof watchSourceFund.isin !== "string" || watchSourceFund.isin === "")
            {
                setSimulation(false);
                setLoadingSimulation(false);
                setFormError({field: "simulation", message: "No se ha podido obtener la simulación de tiempos de reembolso. Inténtelo de nuevo."});
                return;
            }
            FundService.getRefundEstimationDates(watchSourceFund.isin).then((response: any) =>
            {
                if(!mounted.current)
                {
                    return;
                }
                if(!hasCompleteRefundEstimationDates(response))
                {
                    setSimulation(false);
                    setFormError({field: "simulation", message: "No se ha podido obtener la simulación de tiempos de reembolso. Inténtelo de nuevo."});
                    return;
                }
                setRefundDates(
                {
                    ventaValor: response.ventaValor,
                    ventaLiquidacion: response.ventaLiquidacion,
                    finalProceso: response.finalProceso,
                });
                setFormError((current) => current?.field === "simulation" ? null : current);
                setSimulation(true);
            })
            .catch(() =>
            {
                if(!mounted.current)
                {
                    return;
                }
                setSimulation(false);
                setFormError({field: "simulation", message: "No se ha podido obtener la simulación de tiempos de reembolso. Inténtelo de nuevo."});
            })
            .finally(() =>
            {
                if(mounted.current)
                {
                    setLoadingSimulation(false);
                }
            });
        }
        else
        {
            setSimulation(false);
        }
    };
    const onSubmit = async (data: any) =>
    {
        setSimulation(false);
        setFormError(null);

        if(getRefundIbanSelectionError(accounts, refundIBAN) !== "")
        {
            setFormError({field: "iban", message: RefundIbanRequiredMessage});
            return;
        }
        if(accountsLoadFailed || !accountsLoaded)
        {
            return;
        }
        if(refundIBAN.length !== 24)
        {
            setFormError({field: "iban", message: "El IBAN debe contener 24 dígitos, con 2 letras al comienzo y 22 números al final."});
            return;
        }
        if(outdated === true && outdatedPrice == null)
        {
            history.push("/savingsPlans");
            return;
        }
        if(data.sourceFund?.isin == null)
        {
            setFormError({field: "sourceFund", message: "Debe seleccionar un fondo de origen."});
            return;
        }
        if(!isRefundAmountType(data.amountType))
        {
            return;
        }
        if(!isIsinInPortfolioFunds(data.sourceFund.isin, portfolioFunds))
        {
            setFormError({field: "sourceFund", message: "El fondo seleccionado no forma parte de tu cartera."});
            return;
        }
        if(data.amountType === "amount" || data.amountType === "titles")
        {
            const limitsUsable = arePositionLimitsUsable(data.amountType, amount, title, portfolioReady);
            const maxValue = data.amountType === "titles" ? title : amount;
            if(!limitsUsable || !isRefundAmountWithinMax(data.amount, maxValue, true))
            {
                return;
            }
        }

        setSending(true);
        const isinToValidate = data.sourceFund.isin?.trim?.() ?? data.sourceFund.isin;
        const alreadyValidated = lastValidatedFundIsinRef.current === isinToValidate;
        if(!alreadyValidated)
        {
            try
            {
                const fund = await FundService.getFund(data.sourceFund.isin);
                if(!fund?.isin)
                {
                    setFormError({field: "sourceFund", message: "El fondo seleccionado no está disponible. Inténtelo de nuevo."});
                    setSending(false);
                    return;
                }

                lastValidatedFundIsinRef.current = isinToValidate;
            }
            catch
            {
                setFormError({field: "sourceFund", message: "El fondo seleccionado no está disponible. Inténtelo de nuevo."});
                setSending(false);
                return;
            }
        }

        if(data.amountType === "total" && typeof data.amount !== "undefined")
        {
            delete data.amount;
        }

        if(data.amountType === "titles" && title < parseFloat(data.amount))
        {
            data.amount = title;
        }

        let refundAmount = 0;
        if(data.amountType === "total")
        {
            refundAmount = amount;
        }
        else if(data.amountType === "titles")
        {
            const pricePerShare = title > 0 ? amount / title : 0;
            refundAmount = (parseFloat(data.amount) || 0) * pricePerShare;
        }
        else if(data.amountType === "amount")
        {
            refundAmount = parseFloat(data.amount) || 0;
        }

        const instrumentId = data.sourceFund?.finametrixId;
        let toSend: any =
        {
            UserIdentityId: user.mainUserIdentity.id,
            FnmPortfolioId: activePlan.portfolio.id,
            OrderType: 1,
            Status: 0,
            // Contrato backend: el mismo instrumentId de Finametrix se envía
            // temporalmente con los dos nombres (raíz e Values.Fund). Unificar cuando el API deje uno.
            InstrumentId: instrumentId,
            Values:
            {
                RefundCode: data.refundKey,
                FundName: data.sourceFund?.name,
                FundIsin: data.sourceFund?.isin.trim(),
                Fund: instrumentId,
                AmountType: data.amountType,
                Amount: data.amount !== undefined ? parseFloat(data.amount) : 0,
                Type: data.amountType,
                Iban: refundIBAN
            }
        };
        if(outdated === true && outdatedPrice != null && activePlan?.id != null)
        {
            toSend.OutdatedPlanId = activePlan.id;
            toSend.OutdatedPlanRecharge = outdatedPrice;
        }
        const doCreateOrder = async () =>
        {
            try
            {
                const response: any = await OrderService.createOrder(toSend);

                if(response?.id)
                {
                    const signatureState: any =
                    {
                        signature: response.signature,
                        refund: response,
                        cancelledFormState:
                        {
                            origin: data.sourceFund,
                            amountType: data.amountType,
                            amount: data.amount,
                            refundIBAN: refundIBAN
                        }
                    };
                    if(outdated === true && activePlan?.id != null)
                    {
                        signatureState.outdated = true;
                        signatureState.outdatedPlanId = activePlan.id;
                        signatureState.endDate = outdatedEndDate;
                        signatureState.daysExpired = outdatedDaysExpired;
                        signatureState.recharge = outdatedPrice;
                        signatureState.refundAmount = refundAmount;
                    }
                    history.push("/refunds/signature", signatureState);
                }
                else if(mounted.current)
                {
                    setFormError({field: "order", message: "Error al crear la orden. Inténtelo de nuevo."});
                }
            }
            catch(err: any)
            {
                if(!mounted.current)
                {
                    return;
                }
                const status = err?.response?.status;
                const data = err?.response?.data;
                const resolved = resolveCreateOrderCatch(status, data);
                if(resolved.kind === "upgrade")
                {
                    history.push("/UpgradeSubscription",
                    {
                        limitExceeded: data,
                    });
                }
                else if(resolved.kind === "invalidRefundCode")
                {
                    setErrorModal(true);
                    setFormError({field: "refundKey", message: resolved.message});
                }
                else if(resolved.kind === "server")
                {
                    setFormError({field: "order", message: ProcessErrorContactMessage});
                }
                else if(resolved.kind === "field")
                {
                    setFormError({field: resolved.field, message: resolved.message});
                }
                else
                {
                    setFormError({field: "order", message: resolved.message});
                }
            }
            finally
            {
                if(mounted.current)
                {
                    setSending(false);
                }
            }
        };
        doCreateOrder();
    };

    if(cancelled === true || outdatedContract === true)
    {
        return <Redirect to="/savingsPlans" />;
    }

    // Mientras se comprueba si hay clave: spinner. El recargo puede ir en paralelo.
    if(firstTime === true)
    {
        return(
            <DocumentTitle title="IronIA - Reembolsos">
                <form className="external-transfer-form" id="refunds-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Lay.Main theme={theme}>
                        {activePlan !== undefined &&
                        (
                            <Lay.TitleRow className="ms-Grid-row">
                                <div className="ms-Grid-col">
                                    <Lay.TitleContainer>
                                        <h2>Reembolsos</h2>
                                    </Lay.TitleContainer>
                                </div>
                                <Lay.SubTitleCol className="ms-Grid-col">
                                    <Lay.SubTitleContainer>
                                        {" "}
                                        {activePlan.name}
                                    </Lay.SubTitleContainer>
                                </Lay.SubTitleCol>
                            </Lay.TitleRow>
                        )}
                        {activePlan === undefined &&
                        (
                            <Lay.TitleContainer>
                                <h2>Reembolsos</h2>
                            </Lay.TitleContainer>
                        )}
                        <Lay.SeparatorTop />
                        <Spinner size={SpinnerSize.medium} className="button-spinner" />
                        <Lay.OuterFooterAreaStart>
                            <Lay.CardButton size="lg" type="button" onClick={() => history.push("/portfolio")}>
                                Volver
                            </Lay.CardButton>
                        </Lay.OuterFooterAreaStart>
                    </Lay.Main>
                </form>
            </DocumentTitle>
        );
    }

    if(refundCodeCheckFailed === true)
    {
        return(
            <DocumentTitle title="IronIA - Reembolsos">
                <form className="external-transfer-form" id="refunds-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Lay.Main theme={theme}>
                        {activePlan !== undefined &&
                        (
                            <Lay.TitleRow className="ms-Grid-row">
                                <div className="ms-Grid-col">
                                    <Lay.TitleContainer>
                                        <h2>Reembolsos</h2>
                                    </Lay.TitleContainer>
                                </div>
                                <Lay.SubTitleCol className="ms-Grid-col">
                                    <Lay.SubTitleContainer>
                                        {" "}
                                        {activePlan.name}
                                    </Lay.SubTitleContainer>
                                </Lay.SubTitleCol>
                            </Lay.TitleRow>
                        )}
                        {activePlan === undefined &&
                        (
                            <Lay.TitleContainer>
                                <h2>Reembolsos</h2>
                            </Lay.TitleContainer>
                        )}
                        <Lay.SeparatorTop />
                        <MessageBar messageBarType={MessageBarType.error} isMultiline={true} styles={{root: {cursor: "default", marginBottom: "1em"}}}>
                            No se ha podido comprobar si tienes clave de reembolso.
                            <RefundsSupportRetryHint indication="que no se pudo comprobar la clave de reembolso" />
                        </MessageBar>
                        <Lay.OuterFooterAreaStart>
                            <Lay.CardButton size="lg" type="button" onClick={() => history.push("/portfolio")}>
                                Volver
                            </Lay.CardButton>
                            <Lay.CardButton size="lg" type="button" onClick={() => checkRefundCode()}>
                                Volver a intentar
                            </Lay.CardButton>
                        </Lay.OuterFooterAreaStart>
                    </Lay.Main>
                </form>
            </DocumentTitle>
        );
    }

    // Sin clave: solo se puede establecer. No se redirige por recargo hasta tenerla.
    if(!hasRefundCode)
    {
        return(
            <DocumentTitle title="IronIA - Reembolsos">
                <form className="external-transfer-form" id="refunds-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Lay.Main theme={theme}>
                        {activePlan !== undefined &&
                        (
                            <Lay.TitleRow className="ms-Grid-row">
                                <div className="ms-Grid-col">
                                    <Lay.TitleContainer>
                                        <h2>Reembolsos</h2>
                                    </Lay.TitleContainer>
                                </div>
                                <Lay.SubTitleCol className="ms-Grid-col">
                                    <Lay.SubTitleContainer>
                                        {" "}
                                        {activePlan.name}
                                    </Lay.SubTitleContainer>
                                </Lay.SubTitleCol>
                            </Lay.TitleRow>
                        )}
                        {activePlan === undefined &&
                        (
                            <Lay.TitleContainer>
                                <h2>Reembolsos</h2>
                            </Lay.TitleContainer>
                        )}
                        <Lay.SeparatorTop />
                        <div className="ms-Grid restarted-ms-Grid">
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12 ms-xl6">
                                    <Lay.CardContainer theme={theme}>
                                        <Lay.CardHeader>
                                            <Lay.CardHeadingContainer>
                                                <h3>Establecer Clave de Reembolso</h3>
                                            </Lay.CardHeadingContainer>
                                        </Lay.CardHeader>
                                        <Lay.CardBody>
                                            <Stack>
                                                <Stack.Item>
                                                    <Controller name="refundKey" control={control} rules={{required: true}}
                                                        render={({field}: any) =>
                                                        (
                                                            <InputRefundsButton disabled label="Clave de Reembolso"
                                                                placeholder="Escriba aquí la Clave de Reembolso"
                                                                buttonTitle="Establecer"
                                                                onChange={(ev: any, name: any, value: any) =>
                                                                {
                                                                    const val = value ?? "";
                                                                    field.onChange(val);
                                                                    if(val !== "")
                                                                    {
                                                                        onRefundCodeEstablished();
                                                                    }
                                                                }}
                                                                valueRefund={refundCode ?? ""}
                                                                handleRefund={handleChange}
                                                            />
                                                        )}
                                                    />
                                                </Stack.Item>
                                            </Stack>
                                        </Lay.CardBody>
                                        <Lay.OuterFooterAreaStartNoMargin>
                                            <Lay.CardButton size="lg" type="button" onClick={() => history.push("/portfolio")}>
                                                Volver
                                            </Lay.CardButton>
                                        </Lay.OuterFooterAreaStartNoMargin>
                                    </Lay.CardContainer>
                                </div>
                            </div>
                        </div>
                    </Lay.Main>
                </form>
            </DocumentTitle>
        );
    }

    // Con clave: si el plan está caducado y no hay recargo, a planes de ahorro.
    if(outdated === true && outdatedRechargeFailed === true)
    {
        return <Redirect to="/savingsPlans" />;
    }

    // Con clave: plan caducado y recargo aún en curso.
    if(outdated === true && outdatedPrice == null)
    {
        return(
            <DocumentTitle title="IronIA - Reembolsos">
                <form className="external-transfer-form" id="refunds-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Lay.Main theme={theme}>
                        {activePlan !== undefined &&
                        (
                            <Lay.TitleRow className="ms-Grid-row">
                                <div className="ms-Grid-col">
                                    <Lay.TitleContainer>
                                        <h2>Reembolsos</h2>
                                    </Lay.TitleContainer>
                                </div>
                                <Lay.SubTitleCol className="ms-Grid-col">
                                    <Lay.SubTitleContainer>
                                        {" "}
                                        {activePlan.name}
                                    </Lay.SubTitleContainer>
                                </Lay.SubTitleCol>
                            </Lay.TitleRow>
                        )}
                        {activePlan === undefined &&
                        (
                            <Lay.TitleContainer>
                                <h2>Reembolsos</h2>
                            </Lay.TitleContainer>
                        )}
                        <Lay.SeparatorTop />
                        <Spinner size={SpinnerSize.medium} className="button-spinner" />
                        <Lay.OuterFooterAreaStart>
                            <Lay.CardButton size="lg" type="button" onClick={() => history.push("/portfolio")}>
                                Volver
                            </Lay.CardButton>
                        </Lay.OuterFooterAreaStart>
                    </Lay.Main>
                </form>
            </DocumentTitle>
        );
    }

    return(
        <DocumentTitle title="IronIA - Reembolsos">
            <form className="external-transfer-form" id="refunds-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <Lay.Main theme={theme}>
                    {activePlan !== undefined &&
                    (
                        <Lay.TitleRow className="ms-Grid-row">
                            <div className="ms-Grid-col">
                                <Lay.TitleContainer>
                                    <h2>Reembolsos</h2>
                                </Lay.TitleContainer>
                            </div>
                            <Lay.SubTitleCol className="ms-Grid-col">
                                <Lay.SubTitleContainer>
                                    {" "}
                                    {activePlan.name}
                                </Lay.SubTitleContainer>
                            </Lay.SubTitleCol>
                        </Lay.TitleRow>
                    )}
                    {activePlan === undefined &&
                    (
                        <Lay.TitleContainer>
                            <h2>Reembolsos</h2>
                        </Lay.TitleContainer>
                    )}
                    <Lay.SeparatorTop />
                    <ErrorModal title="La clave de Reembolso es inválida" showModal={errorModal} btnSubmitText={"Cerrar"} onClose={() => setErrorModal(false)} />
                    <div className="ms-Grid restarted-ms-Grid">
                        {outdated &&
                        (
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12">
                                    <Lay.OutdatedCardContainer theme={theme}>
                                        <Lay.OutdatedCardContent>
                                            <Lay.OutdatedCardText>
                                                <Lay.TextInfoOutdatedInCard>
                                                    Estás realizando un Reembolso de un Plan de Cartera
                                                    que se encuentra caducado. Al realizar este
                                                    reembolso, <b>Diaphanum Valores</b> cobrará la
                                                    cantidad correspondiente a la cuota de servicio
                                                    desde la fecha de caducidad del plan
                                                    <b>
                                                        {outdatedEndDate != null ? ` ${outdatedEndDate}` : ""}
                                                    </b>{" "}
                                                    y que asciende a un total de
                                                    <b>
                                                        {outdatedPrice != null ? ` ${currencyNeutreFormatter.format(outdatedPrice)} €` : ""}
                                                    </b>
                                                    , cantidad que se descontará del reembolso. Puedes
                                                    renovar tu Plan de Cartera, en cuyo caso no se
                                                    descontará cantidad alguna de este reembolso.
                                                </Lay.TextInfoOutdatedInCard>
                                            </Lay.OutdatedCardText>
                                            <Lay.OutdatedCardButton type="button" onClick={() => history.push("/savingsPlans")}>
                                                Renovar Plan
                                            </Lay.OutdatedCardButton>
                                        </Lay.OutdatedCardContent>
                                    </Lay.OutdatedCardContainer>
                                </div>
                            </div>
                        )}
                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl6 ms-xxl6 ms-xxxl6">
                                <Lay.CardContainer theme={theme}>
                                    <Lay.CardHeader>
                                        <Lay.CardHeadingContainer>
                                            <h3>Datos para el Reembolso</h3>
                                        </Lay.CardHeadingContainer>
                                    </Lay.CardHeader>
                                    <Lay.CardBody>
                                        <Stack>
                                            <Stack.Item>
                                                <Controller name="sourceFund" control={control} rules={{required: true}}
                                                    render={({field}: any) =>
                                                    (
                                                        <InvestmentFundFinder ref={field.ref} name={field.name} required
                                                            key={portfolioFundsLoaded ? "loaded" : "pending"}
                                                            labelISIN="ISIN de origen"
                                                            labelName="Nombre del fondo de origen"
                                                            placeholderISIN="Escribe aquí el ISIN de origen..."
                                                            placeholderName="Escribe aquí el nombre del fondo de origen..."
                                                            default={finderDefaultOrigin}
                                                            maxSuggestions={500}
                                                            loadData={loadRefundFinderData}
                                                            onChange={(ev: any, name: any, value: any) =>
                                                            {
                                                                setFormError((current) =>
                                                                {
                                                                    if(current?.field === "sourceFund" || current?.field === "simulation")
                                                                    {
                                                                        return null;
                                                                    }
                                                                    return current;
                                                                });
                                                                setSimulation(false);

                                                                if(value?.isin != null && portfolioFundsLoaded && !isIsinInPortfolioFunds(value.isin, portfolioFunds))
                                                                {
                                                                    setValue("sourceFund", undefined);
                                                                    field.onChange(undefined);
                                                                    return;
                                                                }

                                                                setValue("sourceFund", value);
                                                                field.onChange(value);
                                                            }}
                                                            errorMessage={displayedSourceFundError}
                                                        />
                                                    )}
                                                />
                                            </Stack.Item>
                                            <Stack.Item>
                                                <Lay.StackItemWithMargin>
                                                    <DropdownStyles.Row>
                                                        <DropdownStyles.ColDropdown>
                                                            <Controller name="refundKey" control={control} rules={{required: true}}
                                                                render={({field}: any) =>
                                                                (
                                                                    <InputRefundsButton name={field.name} type="password" canRevealPassword label="Clave de Reembolso" 
                                                                        placeholder="Escriba aquí la Clave de Reembolso" buttonTitle="Establecer" 
                                                                        onChange={(ev: any, name: any, value: any) =>
                                                                        {
                                                                            const val = value ?? "";
                                                                            if(val !== refundCode)
                                                                            {
                                                                                setFormError((current) => current?.field === "refundKey" ? null : current);
                                                                            }

                                                                            setRefundCode(val);
                                                                            setValue("refundKey", val);
                                                                            field.onChange(val);
                                                                        }}
                                                                        valueRefund={refundCode ?? ""}
                                                                        hideButton={hasRefundCode}
                                                                        handleRefund={handleChange}
                                                                        errorMessage={errorFor("refundKey")}
                                                                    />
                                                                )}
                                                            />
                                                        </DropdownStyles.ColDropdown>
                                                        {hasRefundCode &&
                                                        (
                                                            <DropdownStyles.ColButton alignment="center" paddingTop="1.85rem">
                                                                <Lay.LinkNoDecoration to={{pathname: "/refunds/request-key", state: getRefundsFormState()}}>
                                                                    <PrimaryButton styles={
                                                                        {
                                                                            root:
                                                                            {
                                                                                width: "100%",
                                                                                height: "auto",
                                                                                minHeight: 32,
                                                                                padding: "4px 8px",
                                                                            },
                                                                            label:
                                                                            {
                                                                                whiteSpace: "normal",
                                                                                lineHeight: 1.15,
                                                                            },
                                                                            flexContainer: {whiteSpace: "normal"}
                                                                        }}
                                                                    >
                                                                        He olvidado mi clave
                                                                    </PrimaryButton>
                                                                </Lay.LinkNoDecoration>
                                                            </DropdownStyles.ColButton>
                                                        )}
                                                    </DropdownStyles.Row>
                                                </Lay.StackItemWithMargin>
                                            </Stack.Item>
                                            <Stack.Item>
                                                {accountsLoadFailed &&
                                                (
                                                    <React.Fragment>
                                                        <MessageBar messageBarType={MessageBarType.error} isMultiline={true} styles={{root: {cursor: "default", marginBottom: "1em"}}}>
                                                            No se han podido cargar las cuentas autorizadas para el reembolso.
                                                            <RefundsSupportRetryHint indication="que no se pudieron cargar las cuentas corrientes" />
                                                        </MessageBar>
                                                        <PrimaryButton type="button" onClick={() => setIbanRetryNonce((current) => current + 1)} styles={{root: {marginBottom: "1em"}}}>
                                                            Volver a intentar
                                                        </PrimaryButton>
                                                    </React.Fragment>
                                                )}
                                                <DropdownAccounts accounts={accounts} selectedIban={refundIBAN}
                                                    dropdownChange={(newValue: string) =>
                                                    {
                                                        onChangeIBANValue(newValue);
                                                    }}
                                                    errorMessage={displayedIbanError}
                                                    disabled={accountsLoadFailed || !accountsLoaded}
                                                />
                                            </Stack.Item>
                                        </Stack>
                                        <Lay.SeparatorForm />
                                        <Lay.AmountTypeHeading>
                                            Tipo de importe
                                        </Lay.AmountTypeHeading>
                                        <Stack>
                                            <Stack.Item>
                                                <Controller name="amountType" control={control} rules={{required: true}}
                                                    render={({field}: any) =>
                                                    (
                                                        <ChoiceGroup {...field} required options={refundsOptions}
                                                            selectedKey=
                                                            {
                                                                refundsOptions.find((opt) => opt.value === field.value)?.key ?? undefined
                                                            }
                                                            onChange={(_ev, option) =>
                                                            {
                                                                if(option)
                                                                {
                                                                    setAutoSwitchWarning("none");
                                                                    setValue("amount", "");
                                                                    clearErrors("amount");
                                                                    field.onChange((option as any).value);
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Stack.Item>
                                            {watchAmountType === "amount" &&
                                            (
                                                <Stack.Item>
                                                    <AmountDisclaimerWrapper>
                                                        <p>
                                                            El importe indicado en la orden es estimado y puede variar respecto del importe final, 
                                                            según las condiciones efectivas de ejecución, el precio o valor liquidativo aplicable y, 
                                                            en su caso, variaciones por la divisa. Cuando la orden se realice por importe, podrá aplicarse 
                                                            un margen adicional sobre la posición para evitar descubiertos. Si la salida del fondo supera el 90% 
                                                            de la posición del partícipe en el fondo, la orden podrá tramitarse como un reembolso total o ser rechazada. 
                                                            Si no desea que la operación se tramite así, puede realizarla por las participaciones exactas, teniendo en cuenta 
                                                            que el valor liquidativo final no será conocido a priori y el importe final también podrá fluctuar.
                                                        </p>
                                                    </AmountDisclaimerWrapper>
                                                </Stack.Item>
                                            )}
                                            {autoSwitchWarning !== "none" &&
                                            (
                                                <Stack.Item>
                                                    <AutoSwitchWarningWrapper>
                                                        <MessageBar messageBarType={MessageBarType.warning} isMultiline={true} styles={{root: {cursor: "default"}}}>
                                                            {autoSwitchWarning === "full" &&
                                                            (
                                                                <React.Fragment>
                                                                    <strong>¡ATENCIÓN!</strong> Hemos cambiado
                                                                    automáticamente el tipo de operación a{" "}
                                                                    <strong>Total</strong> porque el importe introducido
                                                                    supera el 100% de tu posición en el fondo.
                                                                    El importe que tienes es de{" "}
                                                                    <strong>{currencyNeutreFormatter.format(amount)} €</strong>.
                                                                </React.Fragment>
                                                            )}
                                                            {autoSwitchWarning === "near90" &&
                                                            (
                                                                <React.Fragment>
                                                                    <strong>¡ATENCIÓN!</strong> Hemos cambiado
                                                                    automáticamente el tipo de operación a{" "}
                                                                    <strong>Total</strong> porque el importe introducido supera el 90%
                                                                    de tu posición en el fondo.
                                                                </React.Fragment>
                                                            )}
                                                        </MessageBar>
                                                    </AutoSwitchWarningWrapper>
                                                </Stack.Item>
                                            )}
                                            {(watchAmountType === "amount" || watchAmountType === "titles") &&
                                            (
                                                <Stack.Item>
                                                    <Controller name="amount" control={control} 
                                                        rules={{required: true, min: 0 + Number.MIN_VALUE, validate:
                                                        {
                                                            amountLess: (value?: string) => watchAmountType !== "amount" || isRefundAmountWithinMax(value, amount, arePositionLimitsUsable("amount", amount, title, portfolioReady)),
                                                            titlesLess: (value?: string) => watchAmountType !== "titles" || isRefundAmountWithinMax(value, title, arePositionLimitsUsable("titles", amount, title, portfolioReady)),
                                                        }}}
                                                        render={({field}: any) =>
                                                        (
                                                            <Lay.AmountInputWrapper>
                                                                <TextField name={field.name} className="amountText" required type="number" step="any"
                                                                    label={watchAmountType === "titles" ? "Participaciones" : "Importe"}
                                                                    placeholder={watchAmountType === "titles" ? "Escriba aquí el número de participaciones..." : "Escriba aquí el importe..."}
                                                                    value={field.value ?? ""}
                                                                    onChange={(_ev, newValue) =>
                                                                    {
                                                                        field.onChange(newValue ?? "");
                                                                    }}
                                                                    onBlur={field.onBlur}
                                                                    errorMessage=
                                                                    {
                                                                        errors.amount && 
                                                                            ((watchAmountType === "titles" &&
                                                                            ((typeof errors.amount.type === "string" && errors.amount.type === "required" && "Indique las participaciones a reembolsar.") || 
                                                                            (typeof errors.amount.type === "string" && errors.amount.type === "min" && "Debe indicar un número mayor de participaciones.") || 
                                                                            (typeof errors.amount.type === "string" && errors.amount.type === "titlesLess" && "El valor de las participaciones no puede ser mayor que el número total de participaciones que posea (" + title + ").") || "Introduzca un número de participaciones válido.")) ||
                                                                            (typeof errors.amount.type === "string" && errors.amount.type === "min" && "El importe debe ser mayor de 0.") ||
                                                                            (typeof errors.amount.type === "string" && errors.amount.type === "required" && "Debe indicar el importe a reembolsar.") ||
                                                                            (typeof errors.amount.type === "string" && errors.amount.type === "amountLess" && "El valor del importe no puede ser mayor que el importe máximo que posee del fondo (" + amount + ").") ||
                                                                            "Introduzca un importe válido.")
                                                                    }
                                                                />
                                                            </Lay.AmountInputWrapper>
                                                        )}
                                                    />
                                                </Stack.Item>
                                            )}
                                        </Stack>
                                        {errorFor("order") !== "" &&
                                        (
                                            <MessageBar messageBarType={MessageBarType.error} isMultiline={true} styles={{root: {cursor: "default", marginTop: "1em"}}}>
                                                {errorFor("order")}
                                            </MessageBar>
                                        )}
                                    </Lay.CardBody>
                                    <Lay.OuterFooterArea>
                                        <Lay.CardButtonBack size="lg" type="button" onClick={() => history.push("/portfolio")}>
                                            Volver
                                        </Lay.CardButtonBack>
                                        <Lay.CardButtonSimulate size="lg" disabled={!simulationAvailable} type="button" onClick={handleSimulation}>
                                            Simular
                                        </Lay.CardButtonSimulate>
                                        <Lay.CardButtonSubmit size="lg" type="submit" disabled=
                                            {
                                                sending ||
                                                !portfolioFundsLoaded ||
                                                !isSelectedFundInPortfolio ||
                                                (outdated === true && outdatedPrice == null) ||
                                                watchSourceFund === undefined ||
                                                errors.sourceFund !== undefined ||
                                                watchRefundKey === undefined ||
                                                (watchRefundKey?.length ?? 0) === 0 ||
                                                errors.refundKey !== undefined ||
                                                refundIBAN.length !== 24 ||
                                                ibanSelectionError !== "" ||
                                                accountsLoadFailed ||
                                                !accountsLoaded ||
                                                !isRefundAmountType(watchAmountType) ||
                                                errors.amountType !== undefined ||
                                                (watchAmountType !== "total" &&
                                                (
                                                    !arePositionLimitsUsable(watchAmountType, amount, title, portfolioReady) ||
                                                    watchAmount === undefined ||
                                                    (typeof watchAmount === "string" && watchAmount.trim() === "") ||
                                                    errors.amount !== undefined
                                                ))
                                            }
                                        >
                                            {sending && <Spinner size={SpinnerSize.small} className="button-spinner" />}
                                            Continuar
                                        </Lay.CardButtonSubmit>
                                    </Lay.OuterFooterArea>
                                </Lay.CardContainer>
                            </div>
                            {(loadingSimulation || simulation || errorFor("simulation") !== "") && simulationAvailable === true &&
                            (
                                <React.Fragment>
                                    <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl6 ms-xxl6 ms-xxxl6">
                                        <Lay.CardContainer theme={theme}>
                                            <Lay.CardHeader>
                                                <Lay.CardHeadingContainer>
                                                    <h3>Simulación de Tiempo de Reembolso</h3>
                                                </Lay.CardHeadingContainer>
                                            </Lay.CardHeader>
                                            {errorFor("simulation") !== "" && loadingSimulation !== true &&
                                            (
                                                <MessageBar messageBarType={MessageBarType.error} isMultiline={true} styles={{root: {cursor: "default"}}}>
                                                    {errorFor("simulation")}
                                                </MessageBar>
                                            )}
                                            {(loadingSimulation || simulation) && errorFor("simulation") === "" &&
                                            (
                                                <RefundTimeLine refundEstimationDates={refundDates} isLoading={loadingSimulation} />
                                            )}
                                        </Lay.CardContainer>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </Lay.Main>
            </form>
        </DocumentTitle>
    );
};